import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { EMPTY, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BasicDialogComponent } from '../shared/components/basic-dialog/basic-dialog.component';
import config from '../../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SimpleDialog } from '../shared/models/simple-dialog';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(private dialog: MatDialog, public router: Router) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const originalUrl = req.url;
    const simpleDialog: SimpleDialog = {
      uiTitle: 'Your session has expired',
      uiMessage:
        "To continue, please click 'OK' to be redirected to the login page.",
      redirect: '/auth/logout',
    };

    // Skip token processing for non-API requests
    if (!originalUrl.includes(`${config.API_GATEWAY_ENDPOINT}`)) {
      return next.handle(req);
    }

    return from(fetchAuthSession()).pipe(
      switchMap((session) => {
        const idToken = session.tokens?.idToken;
        const tokenExpirationTime = idToken?.payload.exp;
        const now = Math.floor(Date.now() / 1000);

        // Check for token expiration
        if (tokenExpirationTime && tokenExpirationTime <= now) {
          this.dialog.open(BasicDialogComponent, { data: simpleDialog });
          return EMPTY;
        }

        if (idToken) {
          const modifiedReq = req.clone({
            headers: req.headers.set('Authorization', idToken.toString()),
          });
          return next.handle(modifiedReq);
        }

        // No token available: redirect to login instead of proceeding unauthenticated
        this.dialog.open(BasicDialogComponent, { data: simpleDialog });
        return EMPTY;
      }),
      catchError((error) => {
        // Any error carrying a `status` property is an HTTP response from the
        // backend API (including status 0 for CORS-blocked responses). Re-throw
        // it so the calling service's .catch() handler receives the original
        // error body intact (e.g. 400 with { message: "..." }).
        // Only errors without `status` are Amplify session-fetch failures.
        if (error?.status !== undefined) {
          return throwError(() => error);
        }

        // Auth endpoints can proceed without a token
        if (originalUrl.includes('/auth/') || originalUrl.includes('/login')) {
          return next.handle(req);
        }

        // For all other endpoints, fail the request instead of retrying without
        // a token — prevents a duplicate unauthenticated request being fired.
        return throwError(() => new Error('Authentication session unavailable', { cause: error }));
      })
    );
  }
}
